import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";
import Header from "../../layout/Header";
import ClientTable from "./components/ClientTable";
import FilterToolbar from "./components/FilterToolbar";
import BulkActions from "./components/BulkActions";
import Pagination from "./components/Pagination";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteClientService,
  getClientsService,
} from "redux/services/clients_service";
import { toast } from "react-toastify";
import { useLanguage } from "../../LanguageContext";

const ClientManagement = () => {
  const dispatch = useDispatch();
  const { language } = useLanguage();

  const [selectedClients, setSelectedClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "All",
    dateRange: { start: "", end: "" },
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { clientsList, metaClient } = useSelector((state) => state.clients);

  const permissions = JSON.parse(localStorage.getItem("permissions"));

  console.log(permissions);
  const addClientExists = permissions.some(
    (permission) => permission.name === "create_client"
  );

  const viewClientExists = permissions.some(
    (permission) => permission.name === "view_client"
  );

  // Translations
  const translations = {
    clientManagement: language === "ar" ? "إدارة العملاء" : "Client Management",
    manageClients:
      language === "ar"
        ? "إدارة ومراقبة جميع عملاء مركز اللياقة البدنية"
        : "Manage and monitor all fitness center clients",
    addNewClient: language === "ar" ? "إضافة عميل جديد" : "Add New Client",
    totalClients: language === "ar" ? "إجمالي العملاء" : "Total Clients",
    active: language === "ar" ? "نشط" : "Active",
    endingSoon: language === "ar" ? "ينتهي قريباً" : "Ending Soon",
    expired: language === "ar" ? "منتهي" : "Expired",
  };

  // Mock client data
  const mockClients = [
    // ... (same mock data as before)
  ];

  const itemsPerPage = 6;

  // Filter clients based on search and status
  const filteredClients = useMemo(() => {
    return mockClients.filter((client) => {
      const matchesSearch =
        client.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        client.phone.includes(filters.search) ||
        client.email.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === "All" ||
        client.subscriptionStatus === filters.status;

      let matchesDateRange = true;
      if (filters.dateRange.start && filters.dateRange.end) {
        const clientStartDate = new Date(client.startDate);
        const filterStartDate = new Date(filters.dateRange.start);
        const filterEndDate = new Date(filters.dateRange.end);
        matchesDateRange =
          clientStartDate >= filterStartDate &&
          clientStartDate <= filterEndDate;
      }

      return matchesSearch && matchesStatus && matchesDateRange;
    });
  }, [filters]);

  // Paginate filtered clients
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredClients.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredClients, currentPage]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handlePageChange = (page) => {
    console.log(`Changing to page: ${page}`);
    setCurrentPage(page);
    dispatch(getClientsService({ page: page }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(paginatedClients.map((client) => client.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (clientId, checked) => {
    if (checked) {
      setSelectedClients((prev) => [...prev, clientId]);
    } else {
      setSelectedClients((prev) => prev.filter((id) => id !== clientId));
    }
  };

  const handleDeleteClient = (clientId) => {
    dispatch(deleteClientService(clientId)).then((response) => {
      if (response.payload?.status === 200 || response.payload?.status === 201 || response.payload?.status === 204) {
        toast.success(
          language === "ar" ? "تم الحذف بنجاح" : "Deleted successfully"
        );
        dispatch(getClientsService({ page: 1 }));
      }
    });
  };

  const handleRenewSubscription = (clientId) => {
    console.log("Renewing subscription for client:", clientId);
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on clients:`, selectedClients);
    if (action === "delete") {
      setSelectedClients([]);
    }
  };

  useEffect(() => {
    dispatch(getClientsService({ page: 1 }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-12">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                {translations.clientManagement}
              </h1>
              <p className="text-gray-600">{translations.manageClients}</p>
            </div>
            {addClientExists && (
              <Link
                to="/add-edit-client-profile"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                <Icon name="Plus" size={20} className="mr-2" />
                {translations.addNewClient}
              </Link>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Icon name="Users" size={24} className="text-green-600" />
              </div>
              <div className="ml-3 mr-3">
                <p className="text-sm font-medium text-gray-600">
                  {translations.totalClients}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {metaClient?.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Icon name="CheckCircle" size={24} className="text-green-600" />
              </div>
              <div className="ml-3 mr-3">
                <p className="text-sm font-medium text-gray-600">
                  {translations.active}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    mockClients.filter((c) => c.subscriptionStatus === "Active")
                      .length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Icon name="Clock" size={24} className="text-yellow-600" />
              </div>
              <div className="ml-3 mr-3">
                <p className="text-sm font-medium text-gray-600">
                  {translations.endingSoon}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    mockClients.filter(
                      (c) => c.subscriptionStatus === "Ending Soon"
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Icon name="XCircle" size={24} className="text-red-600" />
              </div>
              <div className="ml-3 mr-3">
                <p className="text-sm font-medium text-gray-600">
                  {translations.expired}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    mockClients.filter(
                      (c) => c.subscriptionStatus === "Expired"
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <FilterToolbar
          filters={filters}
          onFiltersChange={setFilters}
          language={language}
        />

        {/* Bulk Actions */}
        {selectedClients.length > 0 && (
          <BulkActions
            selectedCount={selectedClients.length}
            onBulkAction={handleBulkAction}
          />
        )}

        {/* Client Table */}
        {viewClientExists && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <ClientTable
              clients={clientsList}
              selectedClients={selectedClients}
              onSelectAll={handleSelectAll}
              onSelectClient={handleSelectClient}
              onDeleteClient={handleDeleteClient}
              onRenewSubscription={handleRenewSubscription}
              allSelected={
                selectedClients.length === paginatedClients.length &&
                paginatedClients.length > 0
              }
            />

            {/* Pagination */}
            {metaClient?.total > 9 && (
              <Pagination
                currentPage={metaClient?.current_page}
                totalPages={metaClient?.last_page}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;
